import { t } from "@lingui/macro"
import { observer } from "mobx-react"
import { useCallback, useEffect, useMemo } from "react"

import { KnowledgeBaseListStore } from "./store"

import { ListPage } from "src/components/ListPage"
import { DataGridProTable } from "src/components/Table/DataGridPro"
import { IPageFilterProps } from "src/components/PageFilter"
import { SegmentPickerButton } from "src/components/SegmentPickerButton"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { useInitializer } from "src/lib/initializer"
import {
    getFilterModelForRepository,
    getSortModelForRepository,
} from "src/lib/data-grid-pro"
import { reportError } from "src/lib/report"
import { ConfirmModal } from "src/modals/confirm"
import { KnowledgeItemModal } from "src/modals/knowledge-items"
import { GlobalStore } from "src/store"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import { Repository } from "src/types/channel"
import {
    FilterModel,
    IAdvancedOperations,
    SortModel,
} from "src/types/data-grid-pro"
import { EModalMode } from "src/types/modal/modal"
import { IKnowledgeItem } from "src/views/knowledge-base/pages/list/types"
import { useKnowledgeBaseListColumns } from "src/views/knowledge-base/pages/list/hooks/useKnowledgeBaseListColumns"

const repository: Repository = "knowledge-base"

const View = observer(() => {
    const store = useStore(KnowledgeBaseListStore)
    const globalStore = useStore(GlobalStore)

    const columns = useKnowledgeBaseListColumns(store.agentAccessTypes)

    const advancedOperationsModel: IAdvancedOperations = useMemo(
        () => ({
            pagination: "server",
            filtering: "server",
            sorting: "server",
        }),
        [],
    )

    const advanceQuery = useMemo(() => {
        const filter = getFilterModelForRepository(
            repository,
            globalStore.session.dataGridFilterModel,
        )
        const sort = getSortModelForRepository(
            repository,
            globalStore.session.dataGridSortModel,
        )
        return { sort, filter }
    }, [
        globalStore.session.dataGridFilterModel,
        globalStore.session.dataGridSortModel,
    ])

    const initialized = useInitializer(async () => {
        await store.init(
            globalStore.session.accessGroup.id ?? DEFAULT_ACCESS_GROUP.id,
            advanceQuery,
        )
    }, [globalStore.session.accessGroup])

    useEffect(() => {
        return () => {
            store.dispose()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleNewFeatureClick = useCallback(() => {
        globalStore.modals.open(() => <KnowledgeItemModal />)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleCopyTemplateFeatureClick = useCallback(
        (item) => {
            if (item != null) {
                globalStore.modals.open(() => (
                    <KnowledgeItemModal id={item.id} mode={EModalMode.Copy} />
                ))
            } else {
                reportError(t`global.copy-feature-error`)
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const header = useMemo(
        () => ({
            header: t`knowledge-base.header`,
            breadcrumbs: [
                t`knowledge-base.header`,
                t`knowledge-base.card.knowledge-card`,
            ],
            createOptions: {
                item: t`knowledge-base.create-new`,
                onClick: handleNewFeatureClick,
            },
        }),
        [handleNewFeatureClick],
    )

    const handleSegmentChange = useCallback(
        (segments: number[]) => store.loadSegments(segments, advanceQuery),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [advanceQuery],
    )

    const filter = useMemo(
        (): IPageFilterProps => ({
            actions: (
                <SegmentPickerButton
                    value={store.segments}
                    onChange={handleSegmentChange}
                />
            ),
        }),
        [store.segments, handleSegmentChange],
    )

    const handleFilterChange = useCallback(
        async (model: FilterModel) => {
            const sort = getSortModelForRepository(
                repository,
                globalStore.session.dataGridSortModel,
            )
            await store.query({ sort, filter: model })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [globalStore.session.dataGridSortModel],
    )

    const handleSortChange = useCallback(
        async (model: SortModel) => {
            const filter = getFilterModelForRepository(
                repository,
                globalStore.session.dataGridFilterModel,
            )
            await store.query({ filter, sort: model })
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [globalStore.session.dataGridFilterModel],
    )

    const handleEditItem = useCallback(
        (item: IKnowledgeItem) => {
            globalStore.modals.open(() => <KnowledgeItemModal id={item.id} />)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const handleDeleteItem = useCallback(
        (item: IKnowledgeItem) => {
            globalStore.modals.open(
                () => (
                    <ConfirmModal
                        onConfirm={async (confirmed) => {
                            if (confirmed) {
                                await store.deleteItem(item.id)
                            }
                        }}
                        title={t`knowledge-base-list-view.are-you-sure`}
                        content={`${t`knowledge-base-list-view.confirm-delete-item`} ${
                            item.title
                        }`}
                    />
                ),
                {
                    variant: "slide-up-w600",
                },
            )
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    const rowActionsRenderer = useCallback(
        (item: IKnowledgeItem) => [
            {
                text: t`knowledge-base-list-view.edit-item`,
                onClick: () => handleEditItem(item),
            },
            {
                text: t`knowledge-base-list-view.copy-template-feature`,
                onClick: () => handleCopyTemplateFeatureClick(item),
            },
            {
                text: t`knowledge-base-list-view.delete-item`,
                onClick: () => handleDeleteItem(item),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    return (
        <ListPage header={header} filter={filter} loading={!initialized}>
            <DataGridProTable
                paginator={store.knowledgeItems}
                data={store.knowledgeItems.items}
                columns={columns}
                advancedOperations={advancedOperationsModel}
                onRowClickEvent={(params) => handleEditItem(params.row)}
                rowActionsRenderer={rowActionsRenderer}
                repository={repository}
                loading={!initialized}
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
            />
        </ListPage>
    )
})

export const KnowledgeBaseListView = () => (
    <StoreProvider Store={KnowledgeBaseListStore}>
        <View />
    </StoreProvider>
)
